/* eslint-disable */
// src/layouts/security/RefreshSessionsPanel.js

import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircularProgress from "@mui/material/CircularProgress";
import AppActionDialog from "components/common/AppActionDialog";
import AppSnackbar from "components/common/AppSnackbar";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import RefreshSessionService from "services/refreshSessionService";
import SecurityColumnHeaders from "layouts/security/SecurityColumnHeaders";
import SecuritySectionTitle from "layouts/security/SecuritySectionTitle";
import { dangerOutlinedButtonSx } from "components/common/buttonStyles";
import { formatSecurityDateTime, getDeviceSummary } from "utils/securityDisplay";

const sessionDataColumns = "minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1fr)";
const sessionColumns = `${sessionDataColumns} 9.5rem`;
const sessionColumnTitles = ["Cuenta", "Dispositivo", "Fechas"];
const historyPageSize = 25;
const grayPaginationButtonSx = {
  color: "#fff",
  backgroundColor: "#6b7280",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#4b5563", boxShadow: "none" },
  "&.Mui-disabled": {
    color: "#fff",
    backgroundColor: "#6b7280",
    boxShadow: "none",
    opacity: 1,
    WebkitTextFillColor: "#fff",
  },
  minWidth: 40,
  width: 40,
  height: 40,
  p: 0,
};

function compareSessionsNewestFirst(first, second) {
  const firstStartedAt = Date.parse(first.Created_at) || 0;
  const secondStartedAt = Date.parse(second.Created_at) || 0;
  return secondStartedAt - firstStartedAt || Number(second.Id) - Number(first.Id);
}

function getSessionStatus(session) {
  if (session.Status) return session.Status;
  if (session.Revoked_at) return "revoked";
  const expiresAt = session.Expires_at ? new Date(session.Expires_at) : null;
  if (expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt <= new Date()) return "expired";
  return "active";
}

function sessionUser(session) {
  return (
    session.Username ||
    session.Email ||
    (session.UserId ? `Usuario ${session.UserId}` : "Usuario sin correo")
  );
}

function reasonLabel(reason) {
  if (reason === "logout") return "Cierre del usuario";
  if (reason === "admin_revoke") return "Cierre del administrador";
  return String(reason || "").replace(/_/g, " ");
}

function SessionRow({ session, onClose, busy }) {
  const status = getSessionStatus(session);
  const active = status === "active";
  const revoked = status === "revoked";
  const endingLabel = revoked
    ? session.RevokedReason
      ? reasonLabel(session.RevokedReason)
      : "Cierre"
    : "Vencimiento";

  return (
    <MDBox
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(0, 1fr) minmax(0, 1fr)",
          lg: sessionColumns,
        },
        alignItems: "center",
        gap: { xs: 1.5, sm: 2 },
        p: { xs: 2, sm: 2.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
        textAlign: "left",
      }}
    >
      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="body2" fontWeight="bold" sx={{ overflowWrap: "anywhere" }}>
          {sessionUser(session)}
        </MDTypography>
        {session.Email && session.Username && (
          <MDTypography
            variant="body2"
            color="text"
            display="block"
            sx={{ overflowWrap: "anywhere" }}
          >
            {session.Email}
          </MDTypography>
        )}
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="body2">{getDeviceSummary(session.UserAgent)}</MDTypography>
        <MDTypography
          variant="body2"
          color="text"
          display="block"
          sx={{ overflowWrap: "anywhere" }}
        >
          IP: {session.IpAddress || "Sin dato"}
        </MDTypography>
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="body2" color="text" display="block">
          Inicio {formatSecurityDateTime(session.Created_at)}
        </MDTypography>
        <MDTypography variant="body2" color="text" display="block">
          Último uso{" "}
          {session.LastUsed_at ? formatSecurityDateTime(session.LastUsed_at) : "Sin uso registrado"}
        </MDTypography>
        {!active && (
          <MDTypography variant="body2" color="text" display="block">
            {endingLabel}{" "}
            {formatSecurityDateTime(revoked ? session.Revoked_at : session.Expires_at)}
          </MDTypography>
        )}
      </MDBox>

      {active && (
        <MDBox display="flex" justifyContent="flex-start">
          <Button
            variant="outlined"
            color="error"
            onClick={() => onClose(session)}
            disabled={busy}
            sx={{ ...dangerOutlinedButtonSx, whiteSpace: "nowrap" }}
          >
            Cerrar sesión
          </Button>
        </MDBox>
      )}
    </MDBox>
  );
}

function SessionList({ sessions, onClose, busy }) {
  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      {sessions.map((session) => (
        <SessionRow key={session.Id} session={session} onClose={onClose} busy={busy} />
      ))}
    </MDBox>
  );
}

export default function RefreshSessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [confirm, setConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const loadSessions = async () => {
    setLoading(true);
    setError("");
    try {
      setSessions(await RefreshSessionService.getRefreshSessions());
      setHistoryPage(1);
    } catch (requestError) {
      setError(
        requestError.response?.status === 403
          ? "No tenés permisos para ver sesiones."
          : requestError.response?.data?.message ||
              requestError.response?.data?.error ||
              "No se pudo cargar el listado. Recargá la página para intentarlo de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const closeSession = async (session) => {
    setActionId(session.Id);
    try {
      await RefreshSessionService.revokeRefreshSession(session.Id);
      setSnack({ open: true, message: "Sesión cerrada.", severity: "success" });
      await loadSessions();
      setConfirm(null);
    } catch (requestError) {
      setSnack({
        open: true,
        message:
          requestError.response?.data?.message ||
          requestError.response?.data?.error ||
          "No se pudo cerrar la sesión.",
        severity: "error",
      });
    } finally {
      setActionId(null);
    }
  };

  const sortedSessions = [...sessions].sort(compareSessionsNewestFirst);
  const active = sortedSessions.filter((session) => getSessionStatus(session) === "active");
  const history = sortedSessions.filter((session) => getSessionStatus(session) !== "active");
  const totalHistoryPages = Math.max(1, Math.ceil(history.length / historyPageSize));
  const currentHistoryPage = Math.min(historyPage, totalHistoryPages);
  const visibleHistory = history.slice(
    (currentHistoryPage - 1) * historyPageSize,
    currentHistoryPage * historyPageSize
  );
  const busy = loading || actionId !== null;

  return (
    <MDBox>
      <SecuritySectionTitle mb={3}>Sesiones vigentes</SecuritySectionTitle>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && sessions.length === 0 ? (
        <MDBox display="flex" alignItems="center" gap={1.5} py={2}>
          <CircularProgress size={22} />
          <MDTypography variant="body2">Cargando sesiones...</MDTypography>
        </MDBox>
      ) : sessions.length === 0 ? (
        error ? null : (
          <MDTypography variant="body2" color="text">
            No hay sesiones registradas.
          </MDTypography>
        )
      ) : (
        <>
          {active.length === 0 ? (
            <MDTypography variant="body2" color="text">
              No hay sesiones vigentes.
            </MDTypography>
          ) : (
            <>
              <SecurityColumnHeaders
                columns={sessionColumnTitles}
                gridTemplateColumns={sessionColumns}
              />
              <SessionList sessions={active} onClose={setConfirm} busy={busy} />
            </>
          )}

          {history.length > 0 && (
            <MDBox component="section" mt={3}>
              <SecuritySectionTitle>Historial</SecuritySectionTitle>
              <SecurityColumnHeaders
                columns={sessionColumnTitles}
                gridTemplateColumns={sessionColumns}
              />
              <SessionList sessions={visibleHistory} busy={busy} />
              {history.length > historyPageSize && (
                <MDBox
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  px={3}
                  pt={0.5}
                  pb={0.5}
                >
                  <MDTypography variant="button" color="secondary">
                    {`Pág. ${currentHistoryPage} de ${totalHistoryPages}`}
                  </MDTypography>
                  <Tooltip title="Página anterior" disableInteractive>
                    <span style={{ display: "inline-flex" }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        aria-label="Página anterior"
                        disabled={currentHistoryPage === 1}
                        sx={grayPaginationButtonSx}
                        onClick={() => setHistoryPage(currentHistoryPage - 1)}
                      >
                        <ChevronLeftIcon />
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Página siguiente" disableInteractive>
                    <span style={{ display: "inline-flex" }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        aria-label="Página siguiente"
                        disabled={currentHistoryPage === totalHistoryPages}
                        sx={grayPaginationButtonSx}
                        onClick={() => setHistoryPage(currentHistoryPage + 1)}
                      >
                        <ChevronRightIcon />
                      </Button>
                    </span>
                  </Tooltip>
                </MDBox>
              )}
            </MDBox>
          )}
        </>
      )}

      <AppActionDialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm && !busy) closeSession(confirm);
        }}
        title="¿Cerrar esta sesión?"
        description={
          <>
            {confirm ? sessionUser(confirm) : ""} · {getDeviceSummary(confirm?.UserAgent)}
            <br />
            El usuario tendrá que iniciar sesión nuevamente cuando venza su acceso actual.
          </>
        }
        confirmLabel={actionId !== null ? "Cerrando..." : "Cerrar sesión"}
        confirmColor="error"
        busy={busy}
      />

      <AppSnackbar
        open={snack.open}
        onClose={() => setSnack((previous) => ({ ...previous, open: false }))}
        severity={snack.severity}
        message={snack.message}
      />
    </MDBox>
  );
}
